// src/pages/MojTjedan.jsx
// Mobile-like view za non-admin: jedan tjedan, jedna osoba (po emailu)

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../config/AuthProvider';
import { useLoading } from '../config/LoadingContext';
import Login from '../authPage/Login';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getWeeksInYear, getCurrentWeekIndex } from '../utils/weekUtils';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { formatDecimalHoursSigned } from '../utils/durationUtils';
import '../css/MojTjedan.css';

const DAY_NAMES_HR = ['PON', 'UTO', 'SRI', 'ČET', 'PET', 'SUB', 'NED'];
const DAY_NAMES_EN = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const HOURS_PER_WEEK = 50;

const T = {
  hr: {
    title: 'Moj tjedan',
    year: 'Godina',
    day: 'Dan',
    date: 'Datum',
    glovo: 'Glovo',
    wolt: 'Wolt',
    total: 'Ukupno',
    leftHours: 'Preostalo sati',
    loading: 'Učitavanje...',
    notLinked: 'Niste povezani',
    notLinkedDesc: 'Vaš email nije povezan s niti jednom osobom u sustavu.',
    contactAdmin: 'Kontaktirajte administratora.',
    noWeek: 'Nema dostupnog tjedna.',
  },
  en: {
    title: 'My week',
    year: 'Year',
    day: 'Day',
    date: 'Date',
    glovo: 'Glovo',
    wolt: 'Wolt',
    total: 'Total',
    leftHours: 'Hours left',
    loading: 'Loading...',
    notLinked: 'Not linked',
    notLinkedDesc: 'Your email is not linked to any person in the system.',
    contactAdmin: 'Contact the administrator.',
    noWeek: 'No week available.',
  },
};

function MojTjedan() {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const { showLoading, hideLoading } = useLoading();
  const [person, setPerson] = useState(null);
  const [dailyMetricsData, setDailyMetricsData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('hr');

  const weeks = useMemo(() => getWeeksInYear(selectedYear), [selectedYear]);

  useEffect(() => {
    setSelectedWeekIndex(getCurrentWeekIndex(selectedYear));
  }, [selectedYear]);

  useEffect(() => {
    if (!currentUser) return;
    const fetchPerson = async () => {
      showLoading('Učitavanje...');
      try {
        // 1. Traži po authId (Firebase Auth UID u dokumentu)
        const qAuthId = query(
          collection(db, 'users'),
          where('authId', '==', currentUser.uid)
        );
        const snapAuthId = await getDocs(qAuthId);
        if (!snapAuthId.empty) {
          const doc = snapAuthId.docs[0];
          setPerson({ id: doc.id, ...doc.data() });
          hideLoading();
          setLoading(false);
          return;
        }
        // 2. Traži po authUid (alternativno ime)
        const qAuthUid = query(
          collection(db, 'users'),
          where('authUid', '==', currentUser.uid)
        );
        const snapAuthUid = await getDocs(qAuthUid);
        if (!snapAuthUid.empty) {
          const doc = snapAuthUid.docs[0];
          setPerson({ id: doc.id, ...doc.data() });
          hideLoading();
          setLoading(false);
          return;
        }
        // 3. Pokušaj polje 'uid'
        const qUid = query(
          collection(db, 'users'),
          where('uid', '==', currentUser.uid)
        );
        const snapUid = await getDocs(qUid);
        if (!snapUid.empty) {
          const doc = snapUid.docs[0];
          setPerson({ id: doc.id, ...doc.data() });
          hideLoading();
          setLoading(false);
          return;
        }
        // 3. Fallback: traži po emailu (uvijek lowercase – case insensitive)
        const emailRaw = (currentUser.email || '').trim();
        const emailLower = emailRaw.toLowerCase();
        if (emailLower) {
          const qEmail = query(
            collection(db, 'users'),
            where('email', '==', emailLower)
          );
          const snapEmail = await getDocs(qEmail);
          if (!snapEmail.empty) {
            const doc = snapEmail.docs[0];
            setPerson({ id: doc.id, ...doc.data() });
            hideLoading();
            setLoading(false);
            return;
          }
          // Pokušaj i s originalnim case (za stare podatke)
          if (emailRaw !== emailLower) {
            const qEmailExact = query(
              collection(db, 'users'),
              where('email', '==', emailRaw)
            );
            const snapExact = await getDocs(qEmailExact);
            if (!snapExact.empty) {
              const doc = snapExact.docs[0];
              setPerson({ id: doc.id, ...doc.data() });
            } else {
              setPerson(null);
            }
          } else {
            setPerson(null);
          }
        } else {
          setPerson(null);
        }
      } catch (e) {
        console.error('Greška pri dohvaćanju osobe:', e);
        setPerson(null);
      } finally {
        hideLoading();
        setLoading(false);
      }
    };
    fetchPerson();
  }, [currentUser]);

  useEffect(() => {
    if (!person?.id) return;
    const fetchMetrics = async () => {
      showLoading('Učitavanje sati...');
      try {
        const years = [selectedYear - 1, selectedYear, selectedYear + 1];
        const q = query(
          collection(db, 'dailyMetrics'),
          where('userId', '==', person.id),
          where('year', 'in', years)
        );
        const snap = await getDocs(q);
        const metrics = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDailyMetricsData(metrics);
      } catch (e) {
        console.error('Greška pri dohvaćanju metrika:', e);
      } finally {
        hideLoading();
      }
    };
    fetchMetrics();
  }, [person?.id, selectedYear]);

  if (currentUser && isAdmin) {
    return <Navigate to="/sati" replace />;
  }

  const getMetricForDate = (year, month, day) => {
    const m = dailyMetricsData.find(
      (d) => d.year === year && d.month === month && d.day === day
    );
    if (!m) return { glovoHours: 0, woltHours: 0, totalHours: 0 };
    const glovo = parseFloat(m.glovoHours ?? m.actualWorkingHours ?? 0) || 0;
    const wolt = parseFloat(m.woltHours ?? m.plannedWorkingHours ?? 0) || 0;
    return { glovoHours: glovo, woltHours: wolt, totalHours: glovo + wolt };
  };

  if (!currentUser) return <Login />;

  const t = T[lang];
  const DAY_NAMES = lang === 'hr' ? DAY_NAMES_HR : DAY_NAMES_EN;

  const goPrevWeek = () => {
    if (selectedWeekIndex > 0) {
      setSelectedWeekIndex(selectedWeekIndex - 1);
    } else {
      setSelectedYear(selectedYear - 1);
      setSelectedWeekIndex(getWeeksInYear(selectedYear - 1).length - 1);
    }
  };

  const goNextWeek = () => {
    const nextWeeks = getWeeksInYear(selectedYear);
    if (selectedWeekIndex < nextWeeks.length - 1) {
      setSelectedWeekIndex(selectedWeekIndex + 1);
    } else {
      setSelectedYear(selectedYear + 1);
      setSelectedWeekIndex(0);
    }
  };

  if (loading) {
    return (
      <div className="moj-tjedan-container">
        <div className="moj-tjedan-loading">{t.loading}</div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="moj-tjedan-container">
        <div className="moj-tjedan-error">
          <h2>{t.notLinked}</h2>
          <p>{t.notLinkedDesc} ({currentUser.email})</p>
          <p>{t.contactAdmin}</p>
        </div>
      </div>
    );
  }

  const week = weeks[selectedWeekIndex];
  if (!week) {
    return (
      <div className="moj-tjedan-container">
        <div className="moj-tjedan-error">{t.noWeek}</div>
      </div>
    );
  }

  let weekTotal = 0;
  const dayRows = DAY_NAMES.map((dayName, dayIdx) => {
    const dateInfo = week.dates[dayIdx];
    if (!dateInfo) return null;
    const data = getMetricForDate(dateInfo.year, dateInfo.month, dateInfo.day);
    weekTotal += data.totalHours;
    const datumStr = `${dateInfo.day}.${dateInfo.month}.`;
    return (
      <tr key={dayName} className={dayName === 'NED' ? 'moj-tjedan-row-ned' : ''}>
        <td className="moj-tjedan-cell-dan">{dayName}</td>
        <td className="moj-tjedan-cell-datum">{datumStr}</td>
        <td className="moj-tjedan-cell-glovo">{formatDecimalHoursSigned(data.glovoHours)}</td>
        <td className="moj-tjedan-cell-wolt">{formatDecimalHoursSigned(data.woltHours)}</td>
        <td className="moj-tjedan-cell-ukupno">
          <strong>{formatDecimalHoursSigned(data.totalHours)}</strong>
        </td>
      </tr>
    );
  });

  const leftHours = HOURS_PER_WEEK - weekTotal;

  const weekLabel = `${week.label} (${week.dates[0]?.day}.${week.dates[0]?.month}.–${week.dates[6]?.day}.${week.dates[6]?.month}.)`;

  return (
    <div className="moj-tjedan-container">
      <div className="moj-tjedan-lang-toggle">
        <button
          type="button"
          className={lang === 'hr' ? 'active' : ''}
          onClick={() => setLang('hr')}
        >
          HR
        </button>
        <button
          type="button"
          className={lang === 'en' ? 'active' : ''}
          onClick={() => setLang('en')}
        >
          EN
        </button>
      </div>

      <header className="moj-tjedan-header">
        <h1 className="moj-tjedan-title">{t.title}</h1>
        <p className="moj-tjedan-person">{person.name}</p>
      </header>

      <div className="moj-tjedan-week-nav">
        <button type="button" onClick={goPrevWeek} aria-label="Prethodni tjedan">
          <FaChevronLeft />
        </button>
        <span className="moj-tjedan-week-label">{weekLabel}</span>
        <button type="button" onClick={goNextWeek} aria-label="Sljedeći tjedan">
          <FaChevronRight />
        </button>
      </div>

      <div className="moj-tjedan-controls">
        <label>
          {t.year}:
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(parseInt(e.target.value, 10));
              setSelectedWeekIndex(0);
            }}
          >
            {[selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="moj-tjedan-table-wrapper">
        <table className="moj-tjedan-table">
          <thead>
            <tr>
              <th>{t.day}</th>
              <th>{t.date}</th>
              <th className="moj-tjedan-th-glovo">{t.glovo}</th>
              <th className="moj-tjedan-th-wolt">{t.wolt}</th>
              <th>{t.total}</th>
            </tr>
          </thead>
          <tbody>
            {dayRows}
            <tr className="moj-tjedan-row-left">
              <td colSpan={2} className="moj-tjedan-cell-left-label">{t.leftHours}</td>
              <td colSpan={2}>–</td>
              <td className="moj-tjedan-cell-ukupno">
                <strong>{formatDecimalHoursSigned(leftHours)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MojTjedan;
